import { Checkbox, Select, Text, Textarea } from '@mantine/core';
import { Task } from '@medplum/fhirtypes';
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, useMemo } from 'react';
import dayjs from 'dayjs';
import { alert } from '@/services/alert';
import { MilestoneCode, PreprocedureTaskCode } from './constants';
import { IssuesCell } from './IssuesCell';
import { LookupSource } from './types';
import { PatientCell, ProcedureCell } from './PatientCell';
import { ANESTHESIA_LABEL_TO_STATUS, AnesthesiaLabel, LogisticalRow, toEt } from './rows';

export interface LogisticalTableProps {
  rows: LogisticalRow[];
  unavailable: LookupSource[];
  /** True while a save for this task is in flight, or when tasks could not be read. */
  isTaskLocked: (patientId: string, code: PreprocedureTaskCode) => boolean;
  onTaskChange: (row: LogisticalRow, code: PreprocedureTaskCode, status: Task['status']) => Promise<void>;
  onCommentChange: (row: LogisticalRow, comment: string) => Promise<void>;
}

const ANESTHESIA_OPTIONS = Object.keys(ANESTHESIA_LABEL_TO_STATUS) as AnesthesiaLabel[];

const MILESTONE_COLUMNS: { code: MilestoneCode; header: string }[] = [
  { code: 'initial-wellness-visit', header: 'TWE' },
  { code: 'group-visit-with-your-doctor', header: 'MD group' },
  { code: 'iv-therapy', header: 'IV' },
  { code: 'post-procedure-wellness-check', header: 'PPD1' },
];

function Unknown(): ReactElement {
  return (
    <Text size="sm" c="dimmed">
      —
    </Text>
  );
}

function MilestoneCell({ row, code }: { row: LogisticalRow; code: MilestoneCode }): ReactElement {
  const et = toEt(row.milestones[code]);
  if (!et) {
    return (
      <Text size="sm" c="red">
        Missing
      </Text>
    );
  }
  const warn = code === 'post-procedure-wellness-check' && row.ppd1Warning;
  return (
    <Text size="sm" c={warn ? 'orange' : undefined} title={warn ? 'Not the day after the procedure' : undefined}>
      {et.format('M/D h:mm a')}
    </Text>
  );
}

function TaskCheckbox({
  row,
  code,
  checked,
  disabled,
  onTaskChange,
}: {
  row: LogisticalRow;
  code: PreprocedureTaskCode;
  checked: boolean | null;
  disabled: boolean;
  onTaskChange: LogisticalTableProps['onTaskChange'];
}): ReactElement {
  return (
    <Checkbox
      checked={checked === true}
      disabled={disabled}
      onChange={(e) => void onTaskChange(row, code, e.currentTarget.checked ? 'completed' : 'requested')}
    />
  );
}

export function LogisticalTable({
  rows,
  unavailable,
  isTaskLocked,
  onTaskChange,
  onCommentChange,
}: LogisticalTableProps): ReactElement {
  const unknown = useMemo(() => new Set(unavailable), [unavailable]);
  const columns = useMemo<MRT_ColumnDef<LogisticalRow>[]>(
    () => [
      {
        id: 'issues',
        header: 'Status',
        size: 90,
        accessorFn: (r) => r.issues.length,
        Cell: ({ row }) => <IssuesCell issues={row.original.issues} />,
      },
      {
        id: 'patient',
        header: 'Patient',
        accessorFn: (r) => r.patientName ?? '',
        Cell: ({ row }) => (
          <PatientCell
            patientId={row.original.patientId}
            patientName={row.original.patientName}
            patientDOB={row.original.patientDOB}
          />
        ),
      },
      {
        id: 'procedure',
        header: 'Procedure',
        accessorFn: (r) => r.procedureDate ?? '',
        Cell: ({ row }) => (
          <ProcedureCell
            typeCode={row.original.typeCode}
            typeDisplay={row.original.typeDisplay}
            dateLabel={row.original.procedureDateEt}
            locationName={row.original.locationName}
          />
        ),
      },
      { id: 'provider', header: 'Provider', accessorFn: (r) => r.providerName ?? '' },
      { id: 'navigator', header: 'Navigator', accessorFn: (r) => r.navigatorName ?? '' },
      ...MILESTONE_COLUMNS.map(
        ({ code, header }): MRT_ColumnDef<LogisticalRow> => ({
          id: code,
          header,
          size: 110,
          accessorFn: (r) => r.milestones[code] ?? '',
          Cell: ({ row }) =>
            unknown.has('milestones') ? <Unknown /> : <MilestoneCell row={row.original} code={code} />,
        })
      ),
      {
        id: 'scale',
        header: 'Scale shipped',
        size: 100,
        accessorFn: (r) => r.scaleShippedAt ?? '',
        Cell: ({ row }) =>
          unknown.has('scales') ? (
            <Unknown />
          ) : row.original.scaleShippedAt ? (
            <Text size="sm">{dayjs(row.original.scaleShippedAt).format('M/D/YYYY')}</Text>
          ) : (
            <Text size="sm" c="red">
              No
            </Text>
          ),
      },
      {
        id: 'scaleOrdered',
        header: 'Scale ordered',
        size: 90,
        accessorFn: (r) => String(r.scaleOrdered),
        Cell: ({ row }) => (
          <TaskCheckbox
            row={row.original}
            code="scaleOrdered"
            disabled={isTaskLocked(row.original.patientId, 'scaleOrdered')}
            checked={row.original.scaleOrdered}
            onTaskChange={onTaskChange}
          />
        ),
      },
      {
        id: 'facilityPaperwork',
        header: 'Facility paperwork',
        size: 90,
        accessorFn: (r) => String(r.facilityPaperwork),
        Cell: ({ row }) => (
          <TaskCheckbox
            row={row.original}
            code="facilityPaperwork"
            disabled={isTaskLocked(row.original.patientId, 'facilityPaperwork')}
            checked={row.original.facilityPaperwork}
            onTaskChange={onTaskChange}
          />
        ),
      },
      {
        id: 'anesthesia',
        header: 'Anesthesia',
        size: 140,
        accessorFn: (r) => r.anesthesia.label ?? '',
        Cell: ({ row }) => (
          <Select
            size="xs"
            data={ANESTHESIA_OPTIONS}
            value={row.original.anesthesia.label}
            placeholder={row.original.anesthesia.required ? 'Required' : '—'}
            allowDeselect={false}
            disabled={isTaskLocked(row.original.patientId, 'anesthesiaBooking')}
            onChange={(value) => {
              if (value) {
                void onTaskChange(
                  row.original,
                  'anesthesiaBooking',
                  ANESTHESIA_LABEL_TO_STATUS[value as AnesthesiaLabel]
                );
              }
            }}
          />
        ),
      },
      {
        id: 'labsSent',
        header: 'Labs sent',
        size: 90,
        accessorFn: (r) => String(r.labsSent),
        Cell: ({ row }) => (
          <TaskCheckbox
            row={row.original}
            code="labsSent"
            disabled={isTaskLocked(row.original.patientId, 'labsSent')}
            checked={row.original.labsSent}
            onTaskChange={onTaskChange}
          />
        ),
      },
      {
        id: 'comment',
        header: 'Appointment notes',
        size: 240,
        accessorFn: (r) => r.appointmentComment ?? '',
        Cell: ({ row }) => {
          const original = row.original.appointmentComment ?? '';
          return (
            <Textarea
              key={`${row.original.id}-${original}`}
              size="xs"
              autosize
              minRows={1}
              defaultValue={original}
              onBlur={(e) => {
                const value = e.currentTarget.value.trim();
                if (value === original) {
                  return;
                }
                if (!value) {
                  // book-appointment only writes a non-empty comment, so an empty save would silently keep the old text.
                  e.currentTarget.value = original;
                  alert.warning(
                    'Appointment notes can’t be cleared here. Clear them from the appointment in the calendar.'
                  );
                  return;
                }
                void onCommentChange(row.original, value);
              }}
            />
          );
        },
      },
    ],
    [onTaskChange, onCommentChange, unknown, isTaskLocked]
  );

  const table = useMantineReactTable({
    columns,
    data: rows,
    getRowId: (r) => r.id,
    enablePagination: false,
    enableColumnActions: false,
    enableGlobalFilter: false,
    enableTopToolbar: false,
    enableStickyHeader: true,
    mantineTableContainerProps: { style: { maxHeight: 'calc(100vh - 280px)' } },
  });

  return <MantineReactTable table={table} />;
}
